import { useMemo } from 'react';
import { siFacebook, siTiktok, siZalo } from 'simple-icons/icons';
import { companyInfo } from '../mock';

const getEnvValue = (key) => process.env[key]?.trim() || '';

const normalizeUrl = (rawValue = '') => {
  if (!rawValue) {
    return '';
  }

  const trimmed = rawValue.trim().replace(/^\/+/, '');
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

const sanitizeDigits = (value = '') => value.replace(/\D/g, '');

const extractTikTokHandle = (rawValue = '') => {
  if (!rawValue) {
    return '';
  }

  const normalized = normalizeUrl(rawValue);

  try {
    const url = new URL(normalized);
    const pathSegments = url.pathname.split('/').filter(Boolean);
    const candidate = pathSegments.find((segment) => segment.startsWith('@')) || pathSegments[0];
    return candidate?.replace(/^@/, '') || '';
  } catch (error) {
    return rawValue.replace(/^@/, '');
  }
};

const buildZaloHref = () => {
  const envOAId = sanitizeDigits(getEnvValue('REACT_APP_ZALO_OA_ID'));
  if (envOAId) {
    return `https://zalo.me/${envOAId}`;
  }

  const hotlineCandidates = (companyInfo.hotlines?.length
    ? companyInfo.hotlines
    : [companyInfo.hotline, companyInfo.secondaryHotline]
  ).filter(Boolean);

  const sanitized = sanitizeDigits(hotlineCandidates[0] || '');
  return sanitized ? `https://zalo.me/${sanitized}` : '';
};

export const SocialWidgets = () => {
  const facebookPageUrl = useMemo(() => {
    return normalizeUrl(
      getEnvValue('REACT_APP_FACEBOOK_PAGE_URL') ||
        companyInfo?.socials?.facebookPage ||
        ''
    );
  }, []);

  const tiktokProfileUrl = useMemo(() => {
    return normalizeUrl(
      getEnvValue('REACT_APP_TIKTOK_PROFILE_URL') ||
        companyInfo?.socials?.tiktokProfile ||
        ''
    );
  }, []);

  const tiktokHandle = useMemo(() => extractTikTokHandle(tiktokProfileUrl), [tiktokProfileUrl]);
  const tiktokHref = tiktokHandle ? `https://www.tiktok.com/@${tiktokHandle}` : '';
  const zaloHref = useMemo(() => buildZaloHref(), []);

  if (!facebookPageUrl && !tiktokHref && !zaloHref) {
    return null;
  }

  return (
    <div className="social-fab-stack" role="navigation" aria-label="Kênh mạng xã hội">
      {zaloHref && (
        <a
          href={zaloHref}
          target="_blank"
          rel="noreferrer noopener"
          className="social-fab-button social-fab-zalo"
          aria-label="Zalo"
          title="Zalo"
        >
          <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
            <path d={siZalo.path} />
          </svg>
        </a>
      )}

      {facebookPageUrl && (
        <a
          href={facebookPageUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="social-fab-button social-fab-facebook"
          aria-label="Facebook"
          title="Facebook"
        >
          <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
            <path d={siFacebook.path} />
          </svg>
        </a>
      )}

      {tiktokHref && (
        <a
          href={tiktokHref}
          target="_blank"
          rel="noreferrer noopener"
          className="social-fab-button social-fab-tiktok"
          aria-label="TikTok"
          title="TikTok"
        >
          <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
            <path d={siTiktok.path} />
          </svg>
        </a>
      )}
    </div>
  );
};
